import { Calendar, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { LiksIconButton } from '../../components/liks/LiksIconButton/LiksIconButton';
import { LiksRefreshButton } from '../../components/liks/LiksRefreshButton/LiksRefreshButton';
import { LiksSearch } from '../../components/liks/LiksSearch/LiksSearch';
import { useI18n } from '../../i18n/useI18n';
import type { LacWebsiteCrawlsStatusFilter } from '../../features/lac/lacWebsiteCrawlsListParams';

export type LacWebsiteCrawlsToolbarProps = {
  searchInput: string;
  onSearchChange: (value: string) => void;
  rangeLabel: string;
  onOpenDateDialog: () => void;
  statusFilter: LacWebsiteCrawlsStatusFilter;
  onStatusFilterChange: (value: string) => void;
  statusFilterOptions: Array<{ value: string; label: string }>;
  showTenantFilter: boolean;
  tenantFilter: string;
  onTenantFilterChange: (value: string) => void;
  tenantFilterOptions: Array<{ value: string; label: string }>;
  tenantFilterDisabled: boolean;
  onManualRefresh: () => void;
  jobsLength: number;
  areAllExpanded: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
};

export function LacWebsiteCrawlsToolbar({
  searchInput,
  onSearchChange,
  rangeLabel,
  onOpenDateDialog,
  statusFilter,
  onStatusFilterChange,
  statusFilterOptions,
  showTenantFilter,
  tenantFilter,
  onTenantFilterChange,
  tenantFilterOptions,
  tenantFilterDisabled,
  onManualRefresh,
  jobsLength,
  areAllExpanded,
  onExpandAll,
  onCollapseAll,
}: LacWebsiteCrawlsToolbarProps) {
  const { translate } = useI18n();

  return (
    <div className="lac-website-crawls__toolbar lac-website-crawls__toolbar--sticky">
      <LiksSearch
        className="lac-website-crawls__search"
        value={searchInput}
        onChange={onSearchChange}
        placeholder={translate('lac.websiteCrawls.searchPlaceholder')}
        aria-label={translate('lac.websiteCrawls.searchLabel')}
        clearLabel={translate('common.search.clear')}
        variant="on-light"
      />
      <div className="lac-website-crawls__date-filter">
        <span className="lac-website-crawls__date-label">{rangeLabel}</span>
        <LiksIconButton
          variant="outlined"
          aria-label={translate('common.dateRange.open')}
          onClick={onOpenDateDialog}
        >
          <Calendar strokeWidth={2} aria-hidden />
        </LiksIconButton>
      </div>
      <LiksDropdown
        className="lac-website-crawls__status-filter"
        variant="on-light"
        value={statusFilter}
        onChange={onStatusFilterChange}
        options={statusFilterOptions}
        aria-label={translate('lac.websiteCrawls.statusFilter.label')}
      />
      {showTenantFilter ? (
        <LiksDropdown
          className="lac-website-crawls__tenant-filter"
          variant="on-light"
          value={tenantFilter}
          onChange={onTenantFilterChange}
          options={tenantFilterOptions}
          aria-label={translate('lac.websiteCrawls.tenantFilter.label')}
          disabled={tenantFilterDisabled}
        />
      ) : null}
      <LiksRefreshButton
        className="lac-website-crawls__refresh-button"
        onRefresh={onManualRefresh}
      />
      <LiksIconButton
        className="lac-website-crawls__expand-toggle"
        variant="outlined"
        disabled={jobsLength === 0}
        onClick={areAllExpanded ? onCollapseAll : onExpandAll}
        aria-label={
          areAllExpanded
            ? translate('lac.websiteCrawls.collapseAll')
            : translate('lac.websiteCrawls.expandAll')
        }
        title={
          areAllExpanded
            ? translate('lac.websiteCrawls.collapseAll')
            : translate('lac.websiteCrawls.expandAll')
        }
      >
        {areAllExpanded ? (
          <ChevronsDownUp strokeWidth={2} aria-hidden />
        ) : (
          <ChevronsUpDown strokeWidth={2} aria-hidden />
        )}
      </LiksIconButton>
    </div>
  );
}
